/**
 * src/audio/useAudio.ts
 *
 * React 19 hook encapsulating the AudioEngine singleton.
 * Provides reactive playback state, volume controls, and procedural SFX triggers
 * with safe hydration and unmount hygiene.
 */

import { useState, useEffect, useCallback } from 'react';
import { audioEngine } from './AudioEngine';

export interface UseAudioReturn {
  isPlaying: boolean;
  isTransitioning: boolean;
  volume: number;
  toggleDrone: () => Promise<boolean>;
  setVolume: (v: number) => void;
  playAnvilStrike: () => void;
  playFlameIgnite: () => void;
  playCardDraw: () => void;
}

export function useAudio(): UseAudioReturn {
  const [isPlaying, setIsPlaying] = useState<boolean>(() => audioEngine.isPlaying);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(() => audioEngine.isTransitioning);
  const [volume, setVolumeState] = useState<number>(() => audioEngine.masterVolume);

  useEffect(() => {
    // Subscribe to AudioEngine state broadcasts
    const unsubscribe = audioEngine.subscribe((state) => {
      setIsPlaying(state.isPlaying);
      setIsTransitioning(state.isTransitioning);
      setVolumeState(state.volume);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const toggleDrone = useCallback(async (): Promise<boolean> => {
    return await audioEngine.toggleDrone();
  }, []);

  const setVolume = useCallback((v: number) => {
    audioEngine.setMasterVolume(v);
  }, []);

  const playAnvilStrike = useCallback(() => {
    audioEngine.playAnvilStrike();
  }, []);

  const playFlameIgnite = useCallback(() => {
    audioEngine.playFlameIgnite();
  }, []);

  const playCardDraw = useCallback(() => {
    audioEngine.playCardDraw();
  }, []);

  return {
    isPlaying,
    isTransitioning,
    volume,
    toggleDrone,
    setVolume,
    playAnvilStrike,
    playFlameIgnite,
    playCardDraw,
  };
}
